import { useState, useEffect, useCallback } from "react";

export interface ServiceStatus {
  state: "green" | "yellow" | "red";
  message: string;
  lastError?: string;
  lastErrorAt?: string;
  action?: string;
}

export interface MarketHoursStatus {
  isOpen: boolean;
  opensAt?: string;
  closesAt?: string;
  holiday?: string;
}

export interface SystemStatus {
  zerodha: ServiceStatus;
  ai: ServiceStatus;
  mongo: ServiceStatus;
  redis: ServiceStatus;
  ticker: ServiceStatus;
  telegram: ServiceStatus;
  marketHours: MarketHoursStatus;
}

export function useStatus() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem("bazaar_token");
    try {
      const res = await fetch("/api/v1/status", {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Status fetch failed: ${res.status}`);
      const data = await res.json() as any;

      // Backend returns { services: { zerodha, ai, ... }, marketOpen, marketHours, timestamp }
      // Normalize to flat shape with marketHours added to each service
      const services = data.services || {};
      setStatus({
        zerodha: services.zerodha || { state: "red", message: "Unknown" },
        ai: services.ai || { state: "red", message: "Unknown" },
        mongo: services.mongodb || services.mongo || { state: "red", message: "Unknown" },
        redis: services.redis || { state: "red", message: "Unknown" },
        ticker: services.ticker || { state: "red", message: "Unknown" },
        telegram: services.telegram || { state: "red", message: "Unknown" },
        marketHours: {
          isOpen: data.marketOpen ?? false,
          holiday: data.holiday,
        },
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to fetch status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
}
